"use client";

import { useState, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { pixelTrack } from "@/components/shared/MetaPixelTracker";
import posthog from "posthog-js";
import { readJson, NotJsonError } from "@/lib/read-json";
import { trackLead } from "@/lib/google-ads";
import { storedClickId } from "@/lib/click-ids";
import { storedAiSource } from "@/lib/ai-source";
import { getConsent } from "@/lib/consent";

type FormState = "idle" | "submitting" | "success" | "error";

type Mode = "quote" | "inquiry";

// Multi-file artwork limits. Files ride along as attachments on the
// notification email, so the total is capped well under Resend's per-message
// size limit (base64 inflates ~33%, so 20 MB raw stays safely under 40 MB).
const MAX_ARTWORK_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB per file
const MAX_TOTAL_BYTES = 20 * 1024 * 1024; // 20 MB across all files

export default function ContactForm() {
  const [state, setState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add files to the accumulated set, de-duping by name+size and respecting
  // the count cap. Mirrors the quote flow's multi-file dropzone UX.
  function addFiles(incoming: FileList | File[]) {
    const list = Array.from(incoming);
    if (list.length === 0) return;
    setFiles((prev) => {
      const combined = [...prev];
      for (const f of list) {
        if (combined.length >= MAX_ARTWORK_FILES) break;
        if (combined.some((x) => x.name === f.name && x.size === f.size)) continue;
        combined.push(f);
      }
      return combined;
    });
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  // Allow other pages to deep-link a pre-selected service:
  //   /contact-us?service=Signs%20%26%20Banners
  const searchParams = useSearchParams();
  const defaultService = searchParams.get("service") ?? "";

  // Two modes: "quote" (service-specific request → flows to Printavo) and
  // "inquiry" (general business question → dashboard only). A deep-linked
  // ?service= preselects quote mode.
  const [mode, setMode] = useState<Mode>("quote");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set("inquiryType", mode);

    // Client-side validation
    const firstName = (formData.get("firstName") as string).trim();
    const lastName = (formData.get("lastName") as string).trim();
    const email = (formData.get("email") as string).trim();
    const service = ((formData.get("service") as string) ?? "").trim();
    const message = (formData.get("message") as string).trim();

    if (!firstName || !lastName) {
      setErrorMessage("Please enter your first and last name.");
      setState("error");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      setState("error");
      return;
    }
    if (mode === "quote" && !service) {
      setErrorMessage("Please select a service.");
      setState("error");
      return;
    }
    if (message.length < 20) {
      setErrorMessage(
        mode === "inquiry"
          ? "Please add a little more detail (at least 20 characters)."
          : "Please provide at least 20 characters of project detail.",
      );
      setState("error");
      return;
    }

    if (files.length > MAX_ARTWORK_FILES) {
      setErrorMessage(`You can attach up to ${MAX_ARTWORK_FILES} files.`);
      setState("error");
      return;
    }
    const oversize = files.find((f) => f.size > MAX_FILE_BYTES);
    if (oversize) {
      setErrorMessage(`"${oversize.name}" is larger than 10 MB.`);
      setState("error");
      return;
    }
    if (files.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES) {
      setErrorMessage("Attachments total more than 20 MB. Please remove some files.");
      setState("error");
      return;
    }

    // The file input is unnamed (state-driven), so append each accumulated
    // file under "artwork" for the server to read with getAll("artwork").
    files.forEach((f) => formData.append("artwork", f));
    // Which ad brought them here, for the offline conversion upload once the
    // quote becomes a real job. Absent for everybody who did not arrive on one.
    const click = storedClickId();
    if (click) formData.append("clickId", `${click.param}:${click.value}`);
    // Which AI assistant sent them, if any, so the quote can say so.
    const aiSource = storedAiSource();
    if (aiSource) formData.append("aiSource", aiSource);
    // The server cannot read the banner answer, so it travels with the form.
    // Reject keeps the submission out of Meta's Conversions API.
    const consent = getConsent();
    if (consent) formData.append("adConsent", consent);

    setState("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/contact", { method: "POST", body: formData });
      // Attachments are real artwork, and Cloudflare's WAF has answered those
      // with its own HTML before now; read it as JSON or as an explanation.
      const json = await readJson<{ error?: string; eventId?: string }>(res);
      if (!res.ok) {
        setErrorMessage(json.error ?? "Something went wrong. Please try again.");
        setState("error");
      } else {
        const category =
          mode === "inquiry"
            ? ((formData.get("subject") as string)?.trim() || "General Inquiry")
            : service;
        if (mode === "inquiry") {
          // A general question is not a quote request. Counting it as a Lead
          // taught the ad platforms to find people who ask questions, so it
          // is reported as a Contact and does not count as a conversion.
          pixelTrack("Contact", { content_category: category }, json.eventId);
          window.gtag?.("event", "inquiry_submitted", { inquiry_subject: category });
        } else {
          pixelTrack("Lead", { content_category: category }, json.eventId);
          // The same event to Google. Meta has had this since launch; Google
          // had nothing, which is why a live campaign showed no conversions.
          trackLead({
            category,
            quoteId: json.eventId,
            // Enhanced Conversions: matches the lead to the ad click even when
            // the cookie is gone or the click happened on another device.
            email,
            phone: (formData.get("phone") as string) || null,
          });
        }
        posthog.identify(email, { email });
        posthog.capture("contact_form_submitted", {
          mode,
          service: category,
          has_artwork: files.length > 0,
          artwork_count: files.length,
        });
        setState("success");
      }
    } catch (err) {
      setErrorMessage(
        err instanceof NotJsonError
          ? err.message
          : "Network error. Please check your connection and try again.",
      );
      setState("error");
    }
  }

  if (state === "success") {
    return (
      <div className="border-2 border-green-200 bg-green-50 p-10 text-center">
        <div className="w-12 h-12 bg-green-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-headline text-2xl text-black uppercase mb-2">
          {mode === "inquiry" ? "Inquiry Sent" : "Quote Request Sent"}
        </h3>
        <p className="text-gray-500 text-sm">
          We&apos;ll get back to you within 1 business day. Check your inbox for a confirmation.
        </p>
      </div>
    );
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      {/* Mode toggle: quote request vs. general inquiry */}
      <div>
        <span className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
          How can we help?
        </span>
        <div className="grid grid-cols-2 gap-px bg-gray-200 border-2 border-gray-200">
          {([
            { v: "quote", label: "Request a Quote", sub: "Pricing for a print, sign, or apparel job" },
            { v: "inquiry", label: "General Inquiry", sub: "A question or anything else" },
          ] as const).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setMode(opt.v)}
              className={`text-left px-4 py-3 transition-colors ${
                mode === opt.v ? "bg-brand-orange text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span className="block text-sm font-semibold">{opt.label}</span>
              <span className={`block text-[11px] mt-0.5 ${mode === opt.v ? "text-white/80" : "text-gray-400"}`}>
                {opt.sub}
              </span>
            </button>
          ))}
        </div>
      </div>

      {state === "error" && errorMessage && (
        <div className="bg-red-50 border-2 border-red-200 px-4 py-3 text-red-700 text-sm">
          {errorMessage}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="firstName" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
            First Name <span className="text-brand-orange">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            required
            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
            placeholder="John"
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
            Last Name <span className="text-brand-orange">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            required
            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="email" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
            Email <span className="text-brand-orange">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
            placeholder="john@company.com"
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
            Phone
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
            placeholder="(555) 555-5555"
          />
        </div>
      </div>

      <div>
        <label htmlFor="company" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
          Company / Organization
        </label>
        <input
          type="text"
          id="company"
          name="company"
          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
          placeholder="Acme Corp"
        />
      </div>

      {mode === "quote" ? (
        <div>
          <label htmlFor="service" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
            Service Needed <span className="text-brand-orange">*</span>
          </label>
          <select
            id="service"
            name="service"
            defaultValue={defaultService}
            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900 cursor-pointer"
          >
            <option value="">Select a service…</option>
            <optgroup label="Print & Apparel">
              <option value="Screen Printing">Screen Printing</option>
              <option value="Embroidery">Embroidery</option>
              <option value="Promotional Products">Promotional Products</option>
              <option value="Laser Engraving">Laser Engraving</option>
            </optgroup>
            <optgroup label="Signage & Awards">
              <option value="Signs & Banners">Signs &amp; Banners</option>
              <option value="Vehicle Decals">Vehicle Decals</option>
              <option value="Trophies & Awards">Trophies &amp; Awards</option>
              <option value="Bronze Plaques">Bronze Plaques</option>
            </optgroup>
            <optgroup label="Digital Solutions">
              <option value="Web Development">Web Development</option>
              <option value="SEO">SEO</option>
              <option value="Mobile Apps">Mobile Apps</option>
              <option value="API Integrations">API Integrations</option>
            </optgroup>
            <option value="Other">Other / Not Sure</option>
          </select>
        </div>
      ) : (
        <div>
          <label htmlFor="subject" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
            Subject{" "}
            <span className="normal-case tracking-normal font-normal text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            maxLength={200}
            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
            placeholder="What's this about?"
          />
        </div>
      )}

      <div>
        <label htmlFor="message" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
          {mode === "inquiry" ? "Your Message" : "Project Details"} <span className="text-brand-orange">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          rows={6}
          required
          className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900 resize-none"
          placeholder={
            mode === "inquiry"
              ? "How can we help? Tell us what you're looking for."
              : "Tell us about your project: quantity, timeline, special requirements, etc."
          }
        />
      </div>

      {/* File upload — quote mode only; inquiries don't need artwork */}
      {mode === "quote" && (
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
          Upload Artwork{" "}
          <span className="normal-case tracking-normal font-normal text-gray-400">
            (optional)
          </span>
        </label>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".png,.jpg,.jpeg,.webp,.gif,.pdf,.ai,.eps,.svg"
          className="sr-only"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              addFiles(e.target.files);
              // Reset so re-picking the same filename (after a remove) fires onChange.
              e.target.value = "";
            }
          }}
        />

        {/* Accumulated files. Each row removes individually; the dropzone below
            stays available until the file cap is reached. */}
        {files.length > 0 && (
          <div className="mb-3 space-y-2">
            {files.map((f, i) => (
              <div
                key={`${f.name}-${f.size}-${i}`}
                className="border-2 border-green-200 bg-green-50 px-3 py-2 flex items-center gap-3"
              >
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-700 font-medium truncate flex-1 min-w-0">{f.name}</p>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-xs text-gray-500 hover:text-gray-800 underline flex-shrink-0"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Dropzone. Hides once the file cap is hit. */}
        {files.length < MAX_ARTWORK_FILES && (
          <div
            className="border-2 border-dashed border-gray-300 p-8 text-center hover:border-brand-orange hover:bg-brand-orange-soft/20 transition-all cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
            }}
          >
            <div className="w-10 h-10 bg-gray-100 group-hover:bg-brand-orange-soft flex items-center justify-center mx-auto mb-3 transition-colors">
              <svg className="w-5 h-5 text-gray-400 group-hover:text-brand-orange transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-gray-600 text-sm">
              <span className="text-brand-orange font-semibold">
                {files.length === 0 ? "Click to upload" : "Add another file"}
              </span>{" "}
              or drag and drop
            </p>
            <p className="text-gray-400 text-xs mt-1">PNG, JPG, WEBP, GIF, PDF, AI, EPS, SVG. Up to 10 files, 10 MB each.</p>
          </div>
        )}
      </div>
      )}

      <div className="pt-2">
        <button
          type="submit"
          disabled={state === "submitting"}
          className="btn-outline-dark w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {state === "submitting"
            ? "Sending…"
            : mode === "inquiry"
            ? "Send Inquiry"
            : "Submit Quote Request"}
        </button>
        <p className="text-gray-400 text-xs mt-3">
          We&apos;ll respond within 1 business day. No spam, ever.
        </p>
      </div>
    </form>
  );
}
